import { DATE_PICKER_LABELS, YEAR_PAGE_SIZE, monthTitle } from './UiDatePicker.constants';
import type { CalendarView } from './UiDatePicker.types';

/**
 * Date math on ISO days (`YYYY-MM-DD`). ISO days compare correctly as plain
 * strings, so every range check is a string comparison; `Date` is only built
 * in local time for arithmetic, never parsed from a string.
 */

export type DateRange = {
    min?: string;
    max?: string;
};

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const DOTTED_DAY = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const COMPACT_DAY = /^(\d{2})(\d{2})(\d{4})$/;

function pad(value: number): string {
    return String(value).padStart(2, '0');
}

export function toIso(date: Date): string {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function fromIso(iso: string): Date {
    const [year, month, day] = iso.split('-').map(Number);

    return new Date(year, month - 1, day);
}

export function isIsoDay(value: unknown): value is string {
    return typeof value === 'string' && ISO_DAY.test(value);
}

/** A CMS value may carry a time part; only the day counts. */
export function normalizeIsoDay(value?: string): string {
    const day = value?.slice(0, 10) ?? '';

    return isIsoDay(day) ? day : '';
}

export function todayIso(): string {
    return toIso(new Date());
}

/** TT.MM.JJJJ — the field's display format. */
export function formatDay(iso: string): string {
    if (!isIsoDay(iso)) return '';

    const [year, month, day] = iso.split('-');

    return `${day}.${month}.${year}`;
}

/** Reads TT.MM.JJJJ (also T.M.JJJJ) or TTMMJJJJ; null for anything else and for impossible days. */
export function parseDay(text: string): string | null {
    const match = text.match(DOTTED_DAY) ?? text.match(COMPACT_DAY);
    if (!match) return null;

    const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const date = new Date(year, month - 1, day);
    const exists = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

    return exists ? toIso(date) : null;
}

export function isInRange(iso: string, { min, max }: DateRange): boolean {
    return (!min || iso >= min) && (!max || iso <= max);
}

export function clampToRange(iso: string, { min, max }: DateRange): string {
    if (min && iso < min) return min;
    if (max && iso > max) return max;

    return iso;
}

export function addDays(iso: string, days: number): string {
    const date = fromIso(iso);
    date.setDate(date.getDate() + days);

    return toIso(date);
}

/** Keeps the day of the month where the target month has it, else takes its last day. */
export function addMonths(iso: string, months: number): string {
    const date = fromIso(iso);
    const day = date.getDate();

    date.setDate(1);
    date.setMonth(date.getMonth() + months);
    date.setDate(Math.min(day, daysInMonth(date.getFullYear(), date.getMonth())));

    return toIso(date);
}

/** The same day and month in another year (29 February falls back to the 28th). */
export function withYear(iso: string, year: number): string {
    const date = fromIso(iso);

    return addMonths(iso, (year - date.getFullYear()) * 12);
}

/** The same day in another month of its year. */
export function withMonth(iso: string, month: number): string {
    return addMonths(iso, month - fromIso(iso).getMonth());
}

export function daysInMonth(year: number, month: number): number {
    return new Date(year, month + 1, 0).getDate();
}

/** Monday-first position in the week, 0 = Monday. */
export function weekdayIndex(iso: string): number {
    return (fromIso(iso).getDay() + 6) % 7;
}

/** Every day a month view shows: whole weeks, from the Monday on or before the 1st. */
export function monthGridDays(year: number, month: number): string[] {
    const offset = (new Date(year, month, 1).getDay() + 6) % 7;
    const count = Math.ceil((offset + daysInMonth(year, month)) / 7) * 7;

    return Array.from({ length: count }, (_, index) => toIso(new Date(year, month, 1 - offset + index)));
}

/** Whether any day of the month lies in the range. */
export function isMonthInRange(year: number, month: number, range: DateRange): boolean {
    const first = toIso(new Date(year, month, 1));
    const last = toIso(new Date(year, month, daysInMonth(year, month)));

    return (!range.min || last >= range.min) && (!range.max || first <= range.max);
}

/** Whether any day of the year lies in the range. */
export function isYearInRange(year: number, range: DateRange): boolean {
    return (!range.min || `${year}-12-31` >= range.min) && (!range.max || `${year}-01-01` <= range.max);
}

/** Splits a flat list into rows of `size`. */
export function chunk<T>(items: T[], size: number): T[][] {
    return Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
        items.slice(index * size, index * size + size),
    );
}

/** The first year of the year page that holds `year`. */
export function yearPageStart(year: number): number {
    return year - (year % YEAR_PAGE_SIZE);
}

/** Why the typed text cannot be submitted; empty when it can. */
export function draftMessage(draft: string | null, range: DateRange): string {
    const text = draft?.trim();
    if (!text) return '';

    const iso = parseDay(text);
    if (!iso) return DATE_PICKER_LABELS.invalid;
    if (isInRange(iso, range)) return '';

    if (range.min && range.max) return DATE_PICKER_LABELS.betweenDays(formatDay(range.min), formatDay(range.max));

    return range.min
        ? DATE_PICKER_LABELS.fromDay(formatDay(range.min))
        : DATE_PICKER_LABELS.untilDay(formatDay(range.max ?? ''));
}

type HeadStep = {
    focus: string;
    label: string;
    disabled: boolean;
};

export type CalendarHead = {
    title: string;
    /** The coarser view the title opens; the year view has none. */
    up?: { view: CalendarView; label: string };
    previous: HeadStep;
    next: HeadStep;
};

/** Title and the two steps of the head, per view: a month, a year or a page of years. */
export function calendarHead(view: CalendarView, focus: string, range: DateRange): CalendarHead {
    const date = fromIso(focus);
    const year = date.getFullYear();
    const month = date.getMonth();
    const labels = DATE_PICKER_LABELS;

    if (view === 'days') {
        const previous = fromIso(addMonths(focus, -1));
        const next = fromIso(addMonths(focus, 1));

        return {
            title: monthTitle(year, month),
            up: { view: 'months', label: labels.chooseMonth },
            previous: {
                focus: clampToRange(addMonths(focus, -1), range),
                label: labels.previousMonth,
                disabled: !isMonthInRange(previous.getFullYear(), previous.getMonth(), range),
            },
            next: {
                focus: clampToRange(addMonths(focus, 1), range),
                label: labels.nextMonth,
                disabled: !isMonthInRange(next.getFullYear(), next.getMonth(), range),
            },
        };
    }

    if (view === 'months') {
        return {
            title: String(year),
            up: { view: 'years', label: labels.chooseYear },
            previous: {
                focus: clampToRange(withYear(focus, year - 1), range),
                label: labels.previousYear,
                disabled: !isYearInRange(year - 1, range),
            },
            next: {
                focus: clampToRange(withYear(focus, year + 1), range),
                label: labels.nextYear,
                disabled: !isYearInRange(year + 1, range),
            },
        };
    }

    const start = yearPageStart(year);

    return {
        title: `${start} – ${start + YEAR_PAGE_SIZE - 1}`,
        previous: {
            focus: clampToRange(withYear(focus, year - YEAR_PAGE_SIZE), range),
            label: labels.previousYears,
            disabled: !isYearInRange(start - 1, range),
        },
        next: {
            focus: clampToRange(withYear(focus, year + YEAR_PAGE_SIZE), range),
            label: labels.nextYears,
            disabled: !isYearInRange(start + YEAR_PAGE_SIZE, range),
        },
    };
}

/** Keeps Tab inside the popover; only the enabled buttons in the tab order count. */
export function trapTab(event: { shiftKey: boolean; preventDefault: () => void }, container: HTMLElement | null) {
    if (!container) return;

    const buttons = [...container.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')].filter(
        (button) => button.tabIndex >= 0,
    );
    if (!buttons.length) return;

    const first = buttons[0];
    const last = buttons[buttons.length - 1];

    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
}
